/**
 * Inclusive bounds of the span of a feed's history proven contiguous with its live tail.
 *
 * Persisted per feed as `oldestContiguousCreatedAt` / `newestContiguousCreatedAt`. Scroll-back and
 * scroll-forward pages anchor on these bounds instead of on whatever rows happen to be cached, so a
 * hole left by an earlier session can never be leapt over. See WEB-2197.
 */
export interface ContiguousSpan {
  /** `createdAt` of the oldest item known to be part of the span. */
  oldest: string;
  /** `createdAt` of the newest item known to be part of the span. */
  newest: string;
}

/**
 * How a fetched page relates to the span it is being merged into.
 *
 * - `anchored` — the request used one of the span's own bounds as its cursor, so the page abuts the
 *   span by construction and always extends it.
 * - `tail` — an unanchored request for the newest page. It only extends the span when it overlaps
 *   it; otherwise messages accumulated while the channel was closed sit between the two and the
 *   span has to restart at the page.
 *
 * Only fetched pages move the span. Items arriving over the realtime subscription do not, even
 * though they are usually contiguous: the subscription can reconnect after dropping messages
 * without triggering a re-bootstrap, so trusting it would bury a hole inside the span where
 * nothing can ever detect it. Leaving those items above the ceiling costs a span restart on the
 * next cold open of a channel that received more than a page of them, which the backfill sweep
 * then heals — a re-fetch, not a permanent gap.
 */
export type PageAnchoring = "anchored" | "tail";

function olderOf(a: string, b: string): string {
  return new Date(a).getTime() <= new Date(b).getTime() ? a : b;
}

function newerOf(a: string, b: string): string {
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
}

/**
 * Folds a freshly-fetched page of item timestamps into a feed's contiguous span.
 *
 * @param current - The persisted span; treated as unknown unless both bounds are set.
 * @param pageCreatedAts - `createdAt` of every item the page returned, in any order.
 * @returns The updated span, or `null` when nothing is known yet.
 */
export function mergeContiguousRange(
  current: { oldest?: string | null; newest?: string | null } | null | undefined,
  pageCreatedAts: readonly string[],
  anchoring: PageAnchoring,
): ContiguousSpan | null {
  const span = current?.oldest && current?.newest ? { oldest: current.oldest, newest: current.newest } : null;

  if (pageCreatedAts.length === 0) {
    return span;
  }

  const pageOldest = pageCreatedAts.reduce(olderOf);
  const pageNewest = pageCreatedAts.reduce(newerOf);

  if (span === null) {
    return { oldest: pageOldest, newest: pageNewest };
  }

  if (anchoring === "tail" && new Date(pageOldest).getTime() > new Date(span.newest).getTime()) {
    return { oldest: pageOldest, newest: pageNewest };
  }

  return { oldest: olderOf(span.oldest, pageOldest), newest: newerOf(span.newest, pageNewest) };
}

/**
 * Raises a feed's render-window lower bound to the oldest history proven contiguous with the tail.
 *
 * The OFFSET probe that derives the window walks back over local rows, so on a cache with holes it
 * lands inside a stale island and renders a feed that jumps across the missing range. Clamping
 * keeps those islands hidden until the backfill sweep reaches them, and loosens on its own as each
 * older page moves the floor, so history heals in place with no visible gap.
 *
 * @param windowStartTimestamp The probe-derived bound; `null` means unbounded.
 * @param contiguityFloor The feed's `oldestContiguousCreatedAt`; `null` when no span is known yet.
 * @param deeplinkFloor A deliberate jump into untrusted history, which the clamp must not override.
 * @param loadedFirstPage Whether the feed has reached the beginning of its history, lifting the clamp.
 * @returns The bound to apply to every feed-content query, or `null` for an unbounded window.
 */
export function clampToContiguousHistory({
  windowStartTimestamp,
  contiguityFloor,
  deeplinkFloor,
  loadedFirstPage,
}: {
  windowStartTimestamp: string | null;
  contiguityFloor: string | null | undefined;
  deeplinkFloor: string | null;
  loadedFirstPage: boolean | undefined;
}): string | null {
  if (loadedFirstPage || !contiguityFloor) {
    return windowStartTimestamp;
  }
  const floor = deeplinkFloor ? olderOf(contiguityFloor, deeplinkFloor) : contiguityFloor;
  if (windowStartTimestamp === null) {
    return floor;
  }
  return newerOf(floor, windowStartTimestamp);
}
